import type { ReactElement, RefObject } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { OPEN_INVOICES_LOGIN_PATH, SUPPORT_PATH } from "@/features/invoices/invoice-surface-states";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { SupportReference } from "@/shared/components/support-reference";
import { Alert, AlertDescription, AlertTitle, Button } from "@/shared/components/ui";
import type {
  RegistrationFinancialOutcomeCategory,
  RegistrationFinancialStatus,
} from "./registration-financial-client";
import {
  clearRegistrationPaymentClientSecret,
  holdPaymentSecretAndRedact,
  readRegistrationPaymentClientSecret,
  type HeldRegistrationFinancialStatus,
} from "./registration-payment-secret-holder";
import { isRegistrationFinancialAuthRejection } from "./registration-financial-client";
import {
  clearRegistrationFinancialToken,
  hasRegistrationFinancialToken,
} from "./registration-financial-token-holder";
import { RegistrationPaymentModal } from "./registration-payment-modal";
import { RegistrationReceiptCopyField } from "./registration-receipt-copy";
import {
  getStatusPollingConfig,
  getStripePublishableKey,
  useRegistrationFinancialClient,
} from "./use-registration-financial";

/**
 * CLMS-117 T033: status-driven financial result surface for the Member
 * completion flow. Purely data-driven (no build-time flag): after completion
 * returns `pending_owner_processing`, the flow polls the registration
 * financial status API and renders per the registration-checkout UI contract:
 *
 * - `pending`               → deterministic pending outcome (CLMS-113 behavior preserved)
 * - `awaiting_payment`      → payment step IFF the response carries a payment
 *                             offer WITH `paymentIntentClientSecret` and NOT
 *                             `processing`; an offer marked `processing: true`
 *                             (charge in flight, with or without a secret —
 *                             FR-040) renders the pending-payment surface
 *                             ("still confirming your payment", never a second
 *                             card entry); otherwise the deferred "your dues
 *                             invoice will follow" surface (offer withheld —
 *                             FR-010)
 *                             An offered step presents Pay now OR Pay later (modal
 *                             close = Pay later): Pay later makes no request,
 *                             scrubs card data, and lands on the deferred
 *                             surface — or on the pending-payment surface when a
 *                             payment had already begun in this session
 * - `completed`             → success view with the warm sign-in guidance:
 *                             the Member's credential was created in the same
 *                             registration flow (011-15 FR-050), so a plain
 *                             Sign in link is always safe — never a credential
 *                             CTA (011-21 "Member completion and re-entry")
 * - `actionable_failure`    → user-safe retry/deferred message per category (FR-022)
 * - `deferred_no_action`    → documented safe deferred outcome + support correlation (FR-023);
 *                             `company_subscription_lapsed` → the company-renewal-needed
 *                             surface (standing contract §6.3): renewal and support
 *                             entry points only, never a payment or activation claim
 *
 * Eligibility is never inferred from state alone, pricing is never inferred
 * from variant/status, and no provider URL is constructed. Navigation stays
 * within the existing CLMS-113 patterns (plain /login link; no person/
 * company/handoff IDs in public bearer URLs — status calls ride the
 * session-authenticated financial API).
 */

export interface FinancialHandoffDescriptor {
  status: "pending_owner_processing";
  correlationId: string;
  /**
   * CLMS-117 status-API key. The checked-in completion contract does not
   * carry it yet (its addition is the auth-service contract revision); until
   * it arrives the surface renders the preserved CLMS-113 static pending
   * outcome and never polls. The FR-046 `accessToken` is deliberately NOT
   * part of this descriptor: it is secret bearer material that lives only in
   * the module-memory token holder — never in React state or props (React's
   * double-buffered fibers would retain it in the `alternate` tree).
   * (The PaymentIntent client secret is scoped differently: the financial-
   * status state never holds it, but the mounted payment modal's Elements
   * options keep a copy until the modal unmounts.)
   */
  handoffId?: string;
}

type FlowPhase = "initial" | "verifying";

type SurfaceKind =
  | "pending"
  | "pending_static"
  | "verifying"
  | "verification_exit"
  | "pending_payment"
  | "payment"
  | "completed"
  | "actionable_failure"
  | "deferred"
  | "deferred_documented"
  | "deferred_company_lapsed";

/** Surface kinds a Pay later choice can land on (UI contract "Pay later disposition"). */
type PayLaterOutcome = Extract<SurfaceKind, "deferred" | "pending_payment">;

/**
 * The announcer may key off a finer-grained kind than the visual switch:
 * the authority-lost revert renders the SAME spec-pinned pending_static
 * visuals but must announce distinctly — a user mid-edit needs to hear the
 * secure window ended, not the generic never-had-authority pending text.
 * A revert applied after a FAILED payment confirmation gets its own kind:
 * the revert unmounts the modal in the same commit that would have shown
 * the decline alert, so the announcement is the only channel telling the
 * user the payment did not complete.
 */
type AnnouncementKind =
  | SurfaceKind
  | "completed_sign_in"
  | "authority_lost"
  | "authority_lost_payment_not_completed";

/**
 * Announcement text for the persistently-mounted polite live region. The
 * surfaces themselves mount WITH content (so per-surface live regions never
 * announce — page U5 note); this single region's TEXT changes across phase
 * transitions, which is what screen readers announce.
 */
const SURFACE_ANNOUNCEMENTS: Record<AnnouncementKind, string> = {
  pending: "Membership financial setup in progress.",
  pending_static: "Membership financial setup is still pending. Check back later.",
  verifying: "Payment confirmation in progress. This may take a moment.",
  verification_exit:
    "Your payment is still being confirmed. You'll receive an email receipt once it's complete.",
  pending_payment:
    "Your payment is still being confirmed. You'll receive an email receipt once it's complete.",
  payment: "You can now pay your membership dues.",
  completed: "Membership financial setup finished.",
  completed_sign_in: "Membership financial setup finished. You can sign in.",
  actionable_failure: "Membership setup needs your attention.",
  deferred:
    "Registration complete. Your dues invoice will be emailed to you; pay any time by signing in and choosing Pay now.",
  deferred_documented:
    "No payment is due right now; the team will follow up about your membership.",
  deferred_company_lapsed: "Your company's membership has lapsed. Your company must renew it.",
  authority_lost:
    "Your secure setup window has ended. Membership financial setup is still pending and processing continues; check back later.",
  authority_lost_payment_not_completed:
    "Your payment was not completed. Your secure setup window has ended; membership financial setup is still pending — check back later.",
};

function resolveSurfaceKind({
  status,
  phase,
  verificationWindowElapsed,
  initialWindowElapsed,
  authorityLost,
  payLaterOutcome,
}: {
  status: HeldRegistrationFinancialStatus | null;
  phase: FlowPhase;
  verificationWindowElapsed: boolean;
  initialWindowElapsed: boolean;
  authorityLost: boolean;
  payLaterOutcome: PayLaterOutcome | null;
}): SurfaceKind {
  if (payLaterOutcome !== null) {
    // Pay later (UI contract "Pay later disposition"): the surface is static
    // after render — deferred, or pending-payment when a payment had begun.
    // Checked BEFORE authorityLost: a late 401 (e.g. a receipt-copy request
    // that started before Pay later) must not change a static surface.
    return payLaterOutcome;
  }
  if (authorityLost) {
    // FR-046 expiry UX: a 401 scrubbed the handoff token — the deterministic
    // static pending surface, exactly the missing-handoffId fallback. No
    // error surface, no silent refresh loop.
    return "pending_static";
  }
  if (status?.state === "completed") {
    return "completed";
  }
  if (status?.state === "actionable_failure") {
    return "actionable_failure";
  }
  if (status?.state === "deferred_no_action") {
    return status.outcomeCategory === "company_subscription_lapsed"
      ? "deferred_company_lapsed"
      : "deferred_documented";
  }
  // null / pending / awaiting_payment from here down.
  if (phase === "verifying") {
    // Poll-budget exit (amended UI contract, DECIDED 2026-09-04): the payment
    // was confirmed but fulfillment hasn't been reported within the window —
    // the exit surface tells the member the payment is still being confirmed.
    // The budget check comes FIRST for every non-terminal report (including a
    // regressed `pending` and failed fetches), so a mid-verification report
    // can never strand the member on the spinner past the window.
    return verificationWindowElapsed ? "verification_exit" : "verifying";
  }
  if (status === null || status.state === "pending") {
    // Initial-window elapse with the handoff still pending (or every fetch
    // failed): the static pending presentation — spinning forever would
    // misrepresent a poll that has stopped.
    return initialWindowElapsed ? "pending_static" : "pending";
  }
  const offer = status.payment;
  // `processing: true` is the canonical discriminator (UI contract L38,
  // FR-040): a provider-side charge is in flight, so this is the pending-
  // payment surface — never a renderable offer even when a client secret is
  // echoed (clm-web#242 item 2), and never the deferred (no-offer) surface,
  // whose "invoice will follow" copy would misread a payment already made.
  if (offer !== null && offer.processing) {
    return "pending_payment";
  }
  const offerRenderable =
    offer !== null && offer.clientSecretHeld && getStripePublishableKey() !== null;
  return offerRenderable ? "payment" : "deferred";
}

/**
 * Credential evidence held by the mounting page (006-6 UI contract
 * "Credential and sign-in statements"): `true` only for a warm
 * `MemberCompletionSucceeded` held by this mount or a cold 011-21 status
 * result of `sign_in`. The completed surface claims sign-in ONLY on evidence;
 * it never derives it from the financial status, storage, history, or URL.
 */
const SignInProvenContext = createContext(false);

export function RegistrationFinancialOutcome({
  handoff,
  signInProven,
}: {
  handoff: FinancialHandoffDescriptor | undefined;
  signInProven: boolean;
}): ReactElement | null {
  if (handoff?.status !== "pending_owner_processing") {
    return null;
  }
  if (!handoff.handoffId) {
    // Preserved CLMS-113 deterministic pending outcome: no status polling
    // without the CLMS-117 handoff key.
    return <StaticPendingHandoff correlationId={handoff.correlationId} />;
  }
  return (
    <SignInProvenContext.Provider value={signInProven}>
      <TokenGatedFinancialFlow
        handoffId={handoff.handoffId}
        correlationId={handoff.correlationId}
      />
    </SignInProvenContext.Provider>
  );
}

function TokenGatedFinancialFlow({
  handoffId,
  correlationId,
}: {
  handoffId: string;
  correlationId: string;
}): ReactElement {
  // FR-046: the endpoints require the handoff-scoped bearer — an older auth
  // service that sent no token gets the same static surface. The gate is
  // LATCHED at mount: later scrubs (terminal status, authority-lost, route
  // cleanup) must not demote an already-running flow on a parent re-render;
  // the flow's own state machine owns every post-mount transition.
  const [tokenHeldAtMount] = useState<boolean>(() => hasRegistrationFinancialToken(handoffId));
  if (!tokenHeldAtMount) {
    return <StaticPendingHandoff correlationId={correlationId} />;
  }
  return <FinancialStatusFlow handoffId={handoffId} correlationId={correlationId} />;
}

/** The one support entry for the pending-family surfaces (FR-010/FR-022): the footer "Contact" target. */
function ContactSupportLink(): ReactElement {
  return (
    <Link to={SUPPORT_PATH} className="underline">
      contact support
    </Link>
  );
}

function StaticPendingHandoff({
  correlationId,
  surfaceRef,
}: {
  correlationId?: string;
  surfaceRef?: RefObject<HTMLDivElement | null>;
}): ReactElement {
  return (
    <div
      ref={surfaceRef}
      tabIndex={-1}
      data-testid="registration-pending-static"
      className="mt-4 text-base text-gray-600 focus:outline-none"
    >
      <p>
        <span className="font-medium text-gray-900">Financial setup status:</span> Pending
        processing
      </p>
      {/* Hedged copy: this surface is reached on re-entry, where a payment may or may not have
          been submitted — it claims neither paid, unpaid, nor canceled. */}
      <p className="mt-1">
        A payment you submitted may still be confirming. Either way, we&apos;ll email you what
        happens next. If you have questions, <ContactSupportLink /> and share the reference below.
      </p>
      {correlationId && (
        <p className="mt-1">
          <span className="font-medium text-gray-900">Financial setup reference:</span>{" "}
          {correlationId}
        </p>
      )}
    </div>
  );
}

function FinancialStatusFlow({
  handoffId,
  correlationId,
}: {
  handoffId: string;
  correlationId: string;
}): ReactElement {
  const client = useRegistrationFinancialClient();
  const [status, setStatus] = useState<HeldRegistrationFinancialStatus | null>(null);
  const [phase, setPhase] = useState<FlowPhase>("initial");
  const [verificationWindowElapsed, setVerificationWindowElapsed] = useState(false);
  const [initialWindowElapsed, setInitialWindowElapsed] = useState(false);
  const [authorityLost, setAuthorityLost] = useState(false);
  // V1 (round 3): true only when the queued revert applied after a FAILED
  // confirm — selects the payment-not-completed announcement variant.
  const [revertAfterFailedConfirm, setRevertAfterFailedConfirm] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // Set once the member chooses Pay later (or closes the modal, which is the
  // same choice): the surface is then static for the rest of this mount.
  const [payLaterOutcome, setPayLaterOutcome] = useState<PayLaterOutcome | null>(null);
  const statusRef = useRef<HeldRegistrationFinancialStatus | null>(null);
  statusRef.current = status;
  const sectionRef = useRef<HTMLElement | null>(null);
  const verifyingSurfaceRef = useRef<HTMLDivElement | null>(null);
  const pendingStaticSurfaceRef = useRef<HTMLDivElement | null>(null);
  const completedSurfaceRef = useRef<HTMLDivElement | null>(null);
  const verificationExitSurfaceRef = useRef<HTMLDivElement | null>(null);
  const deferredSurfaceRef = useRef<HTMLDivElement | null>(null);
  const previousSurfaceKindRef = useRef<SurfaceKind | null>(null);
  const confirmingRef = useRef(false);
  const queuedAuthorityLostRef = useRef(false);
  // True while a confirmPayment dispatched in this session has no definitive
  // provider decline: the payment may already be reaching the provider, so
  // Pay later must render the pending-payment surface (UI contract "Pay later
  // disposition"), never the deferred one.
  const paymentBegunRef = useRef(false);
  // Set synchronously by Pay later (or a modal close): from then on the
  // surface is static, so a late authority-lost signal is a no-op and no
  // later status response may re-park a client secret.
  const payLaterChosenRef = useRef(false);

  // FR-046: a 401 from any registration-financial endpoint means the
  // handoff-scoped token is missing/expired — scrub it (idempotent with the
  // client's own scrub) and revert to the static pending surface.
  const applyAuthorityLost = useCallback(
    (afterFailedConfirm = false): void => {
      if (payLaterChosenRef.current) {
        return; // Pay later made the surface static; a late 401 changes nothing.
      }
      clearRegistrationFinancialToken(handoffId);
      clearRegistrationPaymentClientSecret(handoffId);
      setModalOpen(false);
      setRevertAfterFailedConfirm(afterFailedConfirm);
      setAuthorityLost(true);
    },
    [handoffId],
  );

  const handleAuthorityLost = useCallback((): void => {
    if (payLaterChosenRef.current) {
      return;
    }
    if (confirmingRef.current) {
      // Mid-confirmPayment (e.g. a 3DS challenge): tearing down the modal
      // now would unmount the Stripe Elements iframe with the confirmation
      // in flight — queue the revert until the confirm settles.
      queuedAuthorityLostRef.current = true;
      return;
    }
    applyAuthorityLost();
  }, [applyAuthorityLost]);

  // Confirming state lifted from the modal so a parent-initiated teardown
  // can be deferred while stripe.confirmPayment is in flight.
  const handleConfirmingChange = useCallback(
    (confirming: boolean): void => {
      confirmingRef.current = confirming;
      if (!confirming && queuedAuthorityLostRef.current) {
        queuedAuthorityLostRef.current = false;
        // A successful confirm DROPS the queue in handleConfirmed before
        // confirming flips false, so a queued revert applying here means the
        // confirmation FAILED (decline or thrown) — the revert's announcement
        // must carry the payment-not-completed outcome, because it unmounts
        // the modal in the same commit that would have shown the failure alert.
        applyAuthorityLost(true);
      }
    },
    [applyAuthorityLost],
  );

  // Bounded, runtime-configured polling (contract defaults: 5s interval,
  // 2-minute window). The initial phase polls only while the handoff is
  // still `pending`; the verifying phase (post provider confirmation) polls
  // while the API still reports `awaiting_payment`, and when the window
  // elapses it stops and transitions to the same surface re-entry would show
  // (the still-confirming-your-payment surface). The window is an ABSOLUTE deadline raced
  // against every in-flight request (AbortController + timer): a
  // never-settling status request cannot strand the member on a spinner,
  // and a response settling after the deadline exit is ignored.
  useEffect(() => {
    if (authorityLost) {
      return;
    }
    let disposed = false;
    let expired = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const abort = new AbortController();
    const { intervalMs, windowMs } = getStatusPollingConfig();
    const startedAt = Date.now();

    const exitAtDeadline = (): void => {
      expired = true;
      abort.abort();
      if (phase === "verifying") {
        setVerificationWindowElapsed(true);
      } else {
        setInitialWindowElapsed(true);
      }
    };
    // Deadline enforced even mid-request: when it fires, the surface
    // transitions (verification_exit / pending_static) immediately and the
    // in-flight request is aborted.
    const deadlineTimer = setTimeout(() => {
      if (!disposed && !expired) {
        exitAtDeadline();
      }
    }, windowMs);

    const shouldContinue = (latest: Pick<RegistrationFinancialStatus, "state"> | null): boolean => {
      if (latest === null) {
        return true; // transient fetch failure — retry within the window
      }
      if (phase === "verifying") {
        return latest.state === "pending" || latest.state === "awaiting_payment";
      }
      return latest.state === "pending";
    };

    // Polling settled (terminal status, payment offer, auth loss): the
    // deadline no longer applies — a late fire must not flip an interactive
    // or terminal surface to a window-elapsed one.
    const stopPolling = (): void => {
      clearTimeout(deadlineTimer);
    };

    const poll = async (): Promise<void> => {
      let latest: RegistrationFinancialStatus | null = null;
      let authRejected = false;
      try {
        latest = await client.status(handoffId, { signal: abort.signal });
      } catch (error) {
        // 401 is terminal for this flow (FR-046); anything else is a
        // transient/API failure: keep the last known surface and retry
        // within the bounded window. No detail is surfaced (user-safe only).
        authRejected = isRegistrationFinancialAuthRejection(error);
      }
      if (disposed || expired) {
        return; // late settlement after cleanup or the deadline exit
      }
      if (authRejected) {
        stopPolling();
        handleAuthorityLost(); // stops polling: no further timer is armed
        return;
      }
      if (latest !== null) {
        // The offer's client secret parks in the module-memory holder; the
        // financial-status state never holds it (alternate fibers would
        // retain it). It is retained only when this response will render the
        // payment step — an `awaiting_payment` offer that is not `processing`
        // and a configured publishable key (the same conditions
        // resolveSurfaceKind uses for "payment") — and not after Pay later or
        // in the post-confirm verifying phase. Any other response clears this
        // handoff's slot instead of parking an unused secret.
        const rendersPaymentStep =
          latest.state === "awaiting_payment" &&
          latest.payment !== null &&
          !latest.payment.processing &&
          getStripePublishableKey() !== null;
        setStatus(
          holdPaymentSecretAndRedact(
            handoffId,
            latest,
            rendersPaymentStep && phase !== "verifying" && !payLaterChosenRef.current,
          ),
        );
      }
      if (!shouldContinue(latest ?? statusRef.current)) {
        stopPolling();
        return;
      }
      if (Date.now() - startedAt + intervalMs > windowMs) {
        // The budget cannot fit another interval: exit now instead of
        // spinning idle until the absolute deadline fires.
        stopPolling();
        exitAtDeadline();
        return;
      }
      timer = setTimeout(() => {
        void poll();
      }, intervalMs);
    };

    void poll();
    return () => {
      disposed = true;
      abort.abort();
      clearTimeout(deadlineTimer);
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [client, handoffId, phase, authorityLost, handleAuthorityLost]);

  const handleConfirmed = useCallback((): void => {
    // Provider confirmation accepted: show verification-pending and resume
    // status polling. Success renders ONLY once the status API reports
    // `completed` (webhook-verified) — client-side success is never final.
    // A revert queued mid-confirm is dropped: the confirm succeeded without
    // the handoff token (client-side), so verifying proceeds as normal and
    // a genuinely expired token reverts on the next status poll's 401.
    queuedAuthorityLostRef.current = false;
    // The payment step is over: the client secret leaves the holder (the
    // verifying phase never re-parks one).
    clearRegistrationPaymentClientSecret(handoffId);
    setModalOpen(false);
    setVerificationWindowElapsed(false);
    setPhase("verifying");
  }, [handoffId]);

  // The dialog close and the confirm button's unmount land in the same
  // commit, so focus would drop to <body>; move it onto the verification-
  // pending surface (page pattern: tabIndex={-1} + ref focus).
  useEffect(() => {
    if (phase === "verifying") {
      verifyingSurfaceRef.current?.focus();
    }
  }, [phase]);

  // WCAG 2.4.3: the authority-lost revert can unmount the focused element
  // (receipt-copy Save/Clear, the dialog's focus-restore target) in the
  // same commit; move focus onto the reverted surface. Runs only on the
  // revert-from-interactive transition — the initial missing-token render
  // never mounts this flow. Guarded (WCAG 3.2.5): a background 401 while
  // the user works elsewhere on the page must not yank focus — move it only
  // when the revert actually disturbed it (focus fell to body after the
  // unmount, is gone, or still sits inside this flow's subtree).
  useEffect(() => {
    if (!authorityLost) {
      return;
    }
    const active = document.activeElement;
    if (
      active === null ||
      active === document.body ||
      (sectionRef.current?.contains(active) ?? false)
    ) {
      pendingStaticSurfaceRef.current?.focus();
    }
  }, [authorityLost]);

  // FR-046 retention: once the handoff reaches a terminal status no further
  // token-authenticated call exists — the receipt-copy field renders only
  // inside the offered payment step, never on the completed/failure
  // surfaces — so the secret is scrubbed immediately instead of waiting for
  // route unmount.
  useEffect(() => {
    const state = status?.state;
    if (state === "completed" || state === "actionable_failure" || state === "deferred_no_action") {
      clearRegistrationFinancialToken(handoffId);
      clearRegistrationPaymentClientSecret(handoffId);
    }
  }, [status, handoffId]);

  // Route/flow unmount: no client secret outlives the flow in module memory.
  useEffect(() => {
    return () => {
      clearRegistrationPaymentClientSecret(handoffId);
    };
  }, [handoffId]);

  // Pay later disposition (UI contract, Option B): makes NO request — no
  // cancel/void/release and no status call (the status read performs the
  // get-or-create of the PaymentIntent, so it is not a free read). Polling
  // already stopped when the offer arrived; nothing is armed here. The
  // card-data scrub drops the client secret from the module-memory holder
  // (the financial-status state never holds it), scrubs
  // the handoff token from its holder, and unmounts the Element/modal and the
  // receipt-copy input by leaving the payment step — the unmounted modal's
  // Elements options were the only component state holding a copy, so after
  // Pay later no component state holds the secret. Closing the modal is the
  // same choice; the modal blocks dismissal while a confirm is in flight.
  const handlePayLater = useCallback((): void => {
    payLaterChosenRef.current = true;
    clearRegistrationFinancialToken(handoffId);
    clearRegistrationPaymentClientSecret(handoffId);
    setStatus((current) =>
      current?.state === "awaiting_payment" ? { ...current, payment: null } : current,
    );
    setModalOpen(false);
    setPayLaterOutcome(paymentBegunRef.current ? "pending_payment" : "deferred");
  }, [handoffId]);

  const handlePaymentBegunChange = useCallback((begun: boolean): void => {
    paymentBegunRef.current = begun;
  }, []);

  const handleModalOpenChange = useCallback(
    (open: boolean): void => {
      if (open) {
        setModalOpen(true);
        return;
      }
      handlePayLater();
    },
    [handlePayLater],
  );

  const surfaceKind = resolveSurfaceKind({
    status,
    phase,
    verificationWindowElapsed,
    initialWindowElapsed,
    authorityLost,
    payLaterOutcome,
  });

  // WCAG 2.4.3 (mirrors the authority-lost pattern above): the verifying
  // surface is programmatically focused after confirm, and both of its
  // exits — `completed` and the poll-budget `verification_exit` — unmount
  // it in the same commit, dropping focus to <body>. The payment step has
  // the same shape of exit: Pay later or a modal close unmounts the focused
  // "Pay later"/"Pay now" trigger (restored by the dialog on close) or the
  // receipt-copy input as the `deferred` or `pending_payment` surface
  // mounts (UI contract: no focus loss on Pay later). Move focus onto the
  // replacing surface, guarded (WCAG 3.2.5): only when the transition
  // actually disturbed it (focus fell to body, is gone, or still sits
  // inside this flow's subtree).
  useEffect(() => {
    const previous = previousSurfaceKindRef.current;
    previousSurfaceKindRef.current = surfaceKind;
    const verifyingExit =
      previous === "verifying" &&
      (surfaceKind === "completed" || surfaceKind === "verification_exit");
    const paymentExit =
      previous === "payment" && (surfaceKind === "pending_payment" || surfaceKind === "deferred");
    if (!verifyingExit && !paymentExit) {
      return;
    }
    const active = document.activeElement;
    if (
      active === null ||
      active === document.body ||
      (sectionRef.current?.contains(active) ?? false)
    ) {
      const target =
        surfaceKind === "completed"
          ? completedSurfaceRef.current
          : surfaceKind === "deferred"
            ? deferredSurfaceRef.current
            : verificationExitSurfaceRef.current;
      target?.focus();
    }
  }, [surfaceKind]);

  // Same spec-pinned pending_static visuals, distinct announcement for the
  // revert-from-interactive case (authorityLost only ever flips true after
  // an interactive flow was running); the failed-confirm revert additionally
  // announces that the payment did not complete.
  const signInProven = useContext(SignInProvenContext);
  const announcementKind: AnnouncementKind =
    authorityLost && payLaterOutcome === null
      ? revertAfterFailedConfirm
        ? "authority_lost_payment_not_completed"
        : "authority_lost"
      : surfaceKind === "completed" && signInProven
        ? "completed_sign_in"
        : surfaceKind;

  return (
    <section ref={sectionRef} aria-label="Membership financial setup" className="mt-6 text-left">
      {/* ONE persistently-mounted polite live region: its text change is
          the screen-reader announcement for every surface transition. */}
      <p aria-live="polite" className="sr-only" data-testid="registration-financial-announcer">
        {SURFACE_ANNOUNCEMENTS[announcementKind]}
      </p>
      <FinancialSurface
        kind={surfaceKind}
        status={status}
        correlationId={correlationId}
        handoffId={handoffId}
        modalOpen={modalOpen}
        onModalOpenChange={handleModalOpenChange}
        onPayLater={handlePayLater}
        onConfirmed={handleConfirmed}
        onConfirmingChange={handleConfirmingChange}
        onPaymentBegunChange={handlePaymentBegunChange}
        onAuthorityLost={handleAuthorityLost}
        verifyingSurfaceRef={verifyingSurfaceRef}
        pendingStaticSurfaceRef={pendingStaticSurfaceRef}
        completedSurfaceRef={completedSurfaceRef}
        verificationExitSurfaceRef={verificationExitSurfaceRef}
        deferredSurfaceRef={deferredSurfaceRef}
      />
    </section>
  );
}

function FinancialSurface({
  kind,
  status,
  correlationId,
  handoffId,
  modalOpen,
  onModalOpenChange,
  onPayLater,
  onConfirmed,
  onConfirmingChange,
  onPaymentBegunChange,
  onAuthorityLost,
  verifyingSurfaceRef,
  pendingStaticSurfaceRef,
  completedSurfaceRef,
  verificationExitSurfaceRef,
  deferredSurfaceRef,
}: {
  kind: SurfaceKind;
  status: HeldRegistrationFinancialStatus | null;
  correlationId: string;
  handoffId: string;
  modalOpen: boolean;
  onModalOpenChange: (open: boolean) => void;
  onPayLater: () => void;
  onConfirmed: () => void;
  onConfirmingChange: (confirming: boolean) => void;
  onPaymentBegunChange: (begun: boolean) => void;
  onAuthorityLost: () => void;
  verifyingSurfaceRef: RefObject<HTMLDivElement | null>;
  pendingStaticSurfaceRef: RefObject<HTMLDivElement | null>;
  completedSurfaceRef: RefObject<HTMLDivElement | null>;
  verificationExitSurfaceRef: RefObject<HTMLDivElement | null>;
  deferredSurfaceRef: RefObject<HTMLDivElement | null>;
}): ReactElement {
  const supportCorrelation = status?.supportCorrelation ?? correlationId;

  if (kind === "pending") {
    return <PendingSurface />;
  }

  if (kind === "pending_static") {
    return (
      <StaticPendingHandoff correlationId={correlationId} surfaceRef={pendingStaticSurfaceRef} />
    );
  }

  if (kind === "completed") {
    return (
      <CompletedSurface supportCorrelation={supportCorrelation} surfaceRef={completedSurfaceRef} />
    );
  }

  if (kind === "actionable_failure") {
    return (
      <ActionableFailureSurface
        category={status?.state === "actionable_failure" ? status.outcomeCategory : null}
        supportCorrelation={supportCorrelation}
      />
    );
  }

  if (kind === "deferred_documented") {
    return <DeferredSurface supportCorrelation={supportCorrelation} documentedOutcome />;
  }

  if (kind === "deferred_company_lapsed") {
    return <CompanyRenewalNeededSurface supportCorrelation={supportCorrelation} />;
  }

  if (kind === "verifying") {
    return <VerificationPendingSurface surfaceRef={verifyingSurfaceRef} />;
  }

  if (kind === "verification_exit") {
    return (
      <VerificationExitSurface
        supportCorrelation={supportCorrelation}
        surfaceRef={verificationExitSurfaceRef}
      />
    );
  }

  if (kind === "pending_payment") {
    // `awaiting_payment` with `payment.processing: true` (UI contract L38,
    // FR-040): a charge is in flight, so the member sees the same truthful
    // "still confirming your payment" copy as the poll-budget exit — never a
    // second card entry and never the deferred "invoice will follow" surface.
    // Reachable from the initial load (no focus to recover) AND from the
    // payment step via Pay later after a payment has begun, which unmounts
    // the focused payment controls — the parent's guarded focus-restoration
    // effect targets this ref for that transition.
    return (
      <VerificationExitSurface
        supportCorrelation={supportCorrelation}
        surfaceRef={verificationExitSurfaceRef}
      />
    );
  }

  const publishableKey = getStripePublishableKey();
  const offer = status?.state === "awaiting_payment" ? status.payment : null;
  // Read at render from the module-memory holder: the financial-status state
  // never holds it (the mounted modal's Elements options keep a copy until it
  // unmounts). A scrubbed holder (Pay later, confirmed) fails safe to deferred.
  const clientSecret = readRegistrationPaymentClientSecret(handoffId);

  if (kind === "deferred" || offer === null || clientSecret === null || publishableKey === null) {
    // Offer withheld (flag off / fail-closed guard / supersession), a
    // missing publishable-key configuration (fail safe), or the elapsed
    // post-confirmation window: the safe deferred surface.
    return (
      <DeferredSurface supportCorrelation={supportCorrelation} surfaceRef={deferredSurfaceRef} />
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-clm-dark-gray">Membership dues payment</h2>
      <p className="mt-1 text-base text-gray-600">
        Amount due:{" "}
        <span className="font-medium text-gray-900" data-testid="registration-amount-due">
          {offer.amountDue}
        </span>
      </p>
      {/* Exactly two primary choices (UI contract "Payment step"): Pay now
          opens the card entry; Pay later ends the session with no request. */}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Button type="button" className="w-full sm:flex-1" onClick={() => onModalOpenChange(true)}>
          Pay now
        </Button>
        <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onPayLater}>
          Pay later
        </Button>
      </div>
      <RegistrationReceiptCopyField handoffId={handoffId} onUnauthorized={onAuthorityLost} />
      <SupportReference label="Payment reference" correlationId={supportCorrelation} />
      <RegistrationPaymentModal
        open={modalOpen}
        onOpenChange={onModalOpenChange}
        amountDue={offer.amountDue}
        clientSecret={clientSecret}
        publishableKey={publishableKey}
        onConfirmed={onConfirmed}
        onConfirmingChange={onConfirmingChange}
        onPaymentBegunChange={onPaymentBegunChange}
      />
    </div>
  );
}

function PendingSurface(): ReactElement {
  return (
    <div className="flex items-center gap-2 text-base text-gray-600" role="status">
      <ClmIcon
        name="loading"
        decorative
        className="h-5 w-5 animate-spin text-clm-brand-blue motion-reduce:animate-none"
      />
      <span>Financial setup status: pending processing</span>
    </div>
  );
}

function VerificationPendingSurface({
  surfaceRef,
}: {
  surfaceRef: RefObject<HTMLDivElement | null>;
}): ReactElement {
  return (
    <div
      ref={surfaceRef}
      tabIndex={-1}
      data-testid="registration-verification-pending"
      className="flex items-center gap-2 text-base text-gray-600 focus:outline-none"
      role="status"
    >
      <ClmIcon
        name="loading"
        decorative
        className="h-5 w-5 animate-spin text-clm-brand-blue motion-reduce:animate-none"
      />
      <span>Confirming your payment. This may take a moment&hellip;</span>
    </div>
  );
}

/**
 * Still-confirming-your-payment surface, shared by two kinds:
 * - the poll-budget exit (amended UI contract, DECIDED 2026-09-04): the
 *   member confirmed a payment but fulfillment wasn't reported within the
 *   bounded window (focus moves here from the unmounting spinner via
 *   `surfaceRef`);
 * - the pending-payment surface (UI contract L38, FR-040): `awaiting_payment`
 *   arrives with `payment.processing: true` — a charge is already in flight.
 * Option-agnostic copy: still being confirmed, an email receipt will follow,
 * and how to reach support — never "invoice will follow", which would misread
 * a payment already made, and never a second card entry.
 */
function VerificationExitSurface({
  supportCorrelation,
  surfaceRef,
}: {
  supportCorrelation: string;
  surfaceRef?: RefObject<HTMLDivElement | null>;
}): ReactElement {
  return (
    <div
      ref={surfaceRef}
      tabIndex={-1}
      data-testid="registration-verification-exit"
      className="focus:outline-none"
    >
      <Alert role="status" variant="info">
        <AlertTitle>We&apos;re still confirming your payment</AlertTitle>
        <AlertDescription>
          Your payment is still being confirmed — there is nothing more you need to do right now.
          You&apos;ll receive an email receipt once it&apos;s complete. If you have questions,{" "}
          <ContactSupportLink /> and share the reference below.
        </AlertDescription>
      </Alert>
      <SupportReference label="Payment reference" correlationId={supportCorrelation} />
    </div>
  );
}

function CompletedSurface({
  supportCorrelation,
  surfaceRef,
}: {
  supportCorrelation: string;
  surfaceRef: RefObject<HTMLDivElement | null>;
}): ReactElement {
  const signInProven = useContext(SignInProvenContext);
  return (
    <div
      ref={surfaceRef}
      tabIndex={-1}
      data-testid="registration-financial-completed"
      className="focus:outline-none"
    >
      {/* role="status": success confirmation, not an alert (page U5 pattern). */}
      <Alert role="status" variant="success">
        <AlertTitle>Your membership is set up</AlertTitle>
        <AlertDescription>
          Financial setup is complete.
          {signInProven && " You can now sign in with your registered email."}
        </AlertDescription>
      </Alert>
      {/* 006-6 UI contract: sign-in copy renders ONLY on credential evidence
          held by the mounting page (warm MemberCompletionSucceeded); the
          mounting page owns the single "Sign in" link beneath this surface
          (011-15 UI contract L164 — pending financial status primary, Sign
          in secondary), so this surface never renders a second one and never
          a credential CTA. */}
      <SupportReference label="Payment reference" correlationId={supportCorrelation} />
    </div>
  );
}

const ACTIONABLE_FAILURE_COPY: Partial<Record<RegistrationFinancialOutcomeCategory, string>> = {
  payment_retry_available:
    "Your payment didn't complete and no charge was made. Your registration is saved — your dues invoice will follow by email so you can complete payment.",
  payment_deferred:
    "Your payment couldn't be completed right now. Your registration is saved and your dues invoice will follow by email.",
  support_required:
    "We need to review your registration before your membership can be set up. Contact support and share the reference below.",
};

function ActionableFailureSurface({
  category,
  supportCorrelation,
}: {
  category: RegistrationFinancialOutcomeCategory | null;
  supportCorrelation: string;
}): ReactElement {
  const copy =
    (category !== null ? ACTIONABLE_FAILURE_COPY[category] : undefined) ??
    ACTIONABLE_FAILURE_COPY.support_required;
  return (
    <div>
      <Alert variant="warning">
        <AlertTitle>Your membership setup needs attention</AlertTitle>
        <AlertDescription>{copy}</AlertDescription>
      </Alert>
      <SupportReference label="Payment reference" correlationId={supportCorrelation} />
    </div>
  );
}

/**
 * `deferred_no_action` / `company_subscription_lapsed` (standing contract §6.3,
 * DECIDED): the company renews, not the member. Renewal entry = the ordinary
 * sign-in that lands on the open-invoice list (the company's renewal invoice
 * lives there, FR-031); support entry = the shared support link. No payment
 * control, no activation or sign-in claim for the invitee.
 */
function CompanyRenewalNeededSurface({
  supportCorrelation,
}: {
  supportCorrelation: string;
}): ReactElement {
  return (
    <div>
      <Alert role="status" variant="warning">
        <AlertTitle>Your company&apos;s membership needs renewal</AlertTitle>
        <AlertDescription>
          Your company&apos;s membership has lapsed. Your company must renew it; there is nothing to
          pay on this page. If you manage your company&apos;s membership,{" "}
          <Link to={OPEN_INVOICES_LOGIN_PATH} className="underline">
            sign in to view invoices
          </Link>{" "}
          and renew it. Otherwise, <ContactSupportLink /> and share the reference below.
        </AlertDescription>
      </Alert>
      <SupportReference label="Payment reference" correlationId={supportCorrelation} />
    </div>
  );
}

function DeferredSurface({
  supportCorrelation,
  documentedOutcome = false,
  surfaceRef,
}: {
  supportCorrelation: string;
  documentedOutcome?: boolean;
  surfaceRef?: RefObject<HTMLDivElement | null>;
}): ReactElement {
  return (
    <div
      ref={surfaceRef}
      tabIndex={-1}
      data-testid="registration-deferred"
      className="focus:outline-none"
    >
      <Alert role="status" variant="info">
        <AlertTitle>
          {documentedOutcome
            ? "No payment is needed right now"
            : "Your dues invoice will be emailed to you"}
        </AlertTitle>
        <AlertDescription>
          {documentedOutcome ? (
            <>
              There is nothing more for you to do right now. Our team will follow up about your
              membership; <ContactSupportLink /> with the reference below if you have questions.
            </>
          ) : (
            <>
              Your registration is complete. Your dues invoice will follow by email with
              instructions. To pay any time, sign in, open your invoices, and choose Pay now. If you
              have questions, <ContactSupportLink /> and share the reference below.
            </>
          )}
        </AlertDescription>
      </Alert>
      <SupportReference label="Payment reference" correlationId={supportCorrelation} />
    </div>
  );
}
